import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { CSSProperties } from 'react';

import { GnbRedDotTypes } from '@flipster/apis/v1/red-dot/types';
import { getTradingCompetitionsListApi } from '@flipster/apis/v1/tradingCompetition';
import { TradingCompetition } from '@flipster/apis/v1/tradingCompetition/types';
import { TradingLeaderboardMyRankingItem } from '@flipster/apis/v2/trading-leaderboards/types';
import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { tradingLeaderboardMyRankingBunja } from '@flipster/features/src/trading-leaderboard-page/bunja';
import { userPoolEligibilityBunja } from '@flipster/features/src/userpool/user-pool.state';
import { i18nBunja } from '@flipster/i18n';
import { CookieKeys, getCookieBunja } from '@flipster/utils/cookie';

export interface Nav {
  isHide?: boolean;
  redDotKey?: GnbRedDotTypes;
}

export interface LeaderboardNav extends Nav {
  type: 'leaderboard';
  label: string;
  url: string;
  ranking: TradingLeaderboardMyRankingItem | undefined;
}

export interface LinkNav extends Nav {
  type: 'link';
  label: string;
  url: string;
  newTab?: boolean;
}

export interface MenuNav extends Nav {
  type: 'menu';
  label: string; // React node
  children: (LinkNav | LeaderboardNav)[];
  style?: CSSProperties;
}

export interface GroupNav extends Nav {
  type: 'group';
  label: string;
  children: MenuNav[];
}

export type Navigations = LinkNav | MenuNav | GroupNav;

export const separateGroup = (navigations: (MenuNav | LinkNav | GroupNav)[]) => {
  return navigations.flatMap((nav) => {
    if (nav.type === 'group') return nav.children;
    return nav;
  });
};

const navBaseBunja = bunja(() => {
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { isSpotOnlyAtom } = bunja.use(userRegulationBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();
  return { t, isLoggedInAtom, isSpotOnlyAtom };
});

const navAbTestBunja = bunja(() => {
  const { getAbFlagAtom } = bunja.use(abTestBunja);
  const isUsd1PointsDashboardEnabledAtom = getAbFlagAtom('25q3-ti-usd1-points-dashboard', 'treatment');
  const isCopyTradingEnabledAtom = getAbFlagAtom('25q4-copytrading', 'treatment');
  return {
    isUsd1PointsDashboardEnabledAtom,
    isCopyTradingEnabledAtom,
  };
});

export const navBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyAtom } = bunja.use(navBaseBunja);
  const { isCopyTradingEnabledAtom } = bunja.use(navAbTestBunja);

  const { tradeNavAtom } = bunja.use(tradeNavBunja);
  const { earnNavAtom } = bunja.use(earnNavBunja);
  const { historyNavAtom } = bunja.use(historyNavBunja);
  const { moreNavAtom } = bunja.use(moreNavBunja);

  const navAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyAtom);
    const isCopyTradingEnabled = get(isCopyTradingEnabledAtom);
    const tradeNav = get(tradeNavAtom);
    const earnNav = get(earnNavAtom);
    const historyNav = get(historyNavAtom);
    const moreNav = get(moreNavAtom);
    return [
      ...tradeNav,
      { type: 'link', label: t('Navigation.r250401.Market'), url: `/market/${isSpotOnly ? 'spot' : 'perpetual'}` },
      isCopyTradingEnabled
        ? { type: 'link', label: 'Copy', url: '/copy-trading', isHide: isSpotOnly }
        : { type: 'link', label: t('Navigation.r250401.Insights'), url: '/insights', isHide: isSpotOnly },
      ...earnNav,
      { type: 'link', label: t('Navigation.r250401.Assets'), url: '/assets', isHide: !isLoggedIn },
      ...historyNav,
      ...moreNav,
    ];
  });

  return { navAtom };
});

const tradeNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyAtom } = bunja.use(navBaseBunja);
  const { cookieAtom: perpReferralSymbolAtom } = bunja.use(getCookieBunja(CookieKeys.ReferrerSymbol));
  const { cookieAtom: spotReferralSymbolAtom } = bunja.use(getCookieBunja(CookieKeys.SpotReferrerSymbol));

  const tradeNavAtom = atom<Navigations[]>((get) => {
    const isSpotOnly = get(isSpotOnlyAtom);
    const perpTradeUrl = get(perpTradeUrlAtom);
    const spotTradeUrl = get(spotTradeUrlAtom);
    return [
      {
        type: 'menu',
        label: t('Navigation.r250401.Trade'),
        isHide: isSpotOnly,
        children: [
          { type: 'link', label: t('Navigation.r250401.Perpetual'), url: perpTradeUrl },
          { type: 'link', label: t('Navigation.r250401.Spot'), url: spotTradeUrl },
        ],
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Trade'),
        url: spotTradeUrl,
        isHide: !isSpotOnly,
      },
    ];
  });

  const perpTradeUrlAtom = atom<string>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const perpReferralSymbol = get(perpReferralSymbolAtom);
    return isLoggedIn && perpReferralSymbol ? `/trade/perpetual/${perpReferralSymbol}` : '/trade/perpetual';
  });

  const spotTradeUrlAtom = atom<string>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const spotReferralSymbol = get(spotReferralSymbolAtom);
    return isLoggedIn && spotReferralSymbol ? `/trade/spot/${spotReferralSymbol}` : '/trade/spot';
  });

  return { tradeNavAtom };
});

const earnNavBunja = bunja(() => {
  const { t, isSpotOnlyAtom } = bunja.use(navBaseBunja);
  const { userPoolEligibilityAtom } = bunja.use(userPoolEligibilityBunja);

  const earnNavAtom = atom<Navigations[]>((get) => {
    const isSpotOnly = get(isSpotOnlyAtom);
    const userPoolEligibility = get(userPoolEligibilityAtom);
    return [
      {
        type: 'menu',
        label: t('Navigation.r250401.Earn_plus'),
        isHide: isSpotOnly,
        children: [
          { type: 'link', label: t('Navigation.r250401.Earn'), url: '/earn' },
          {
            type: 'link',
            label: t('Navigation.r250829.New_joiner_pool'),
            url: `/userpool/${userPoolEligibility?.id}`,
            isHide: !userPoolEligibility?.eligible,
          },
          { type: 'link', label: t('Navigation.r250401.Launchpool'), url: '/launchpool' },
        ],
      },
    ];
  });

  return { earnNavAtom };
});

const historyNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyAtom } = bunja.use(navBaseBunja);

  const historyNavAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyAtom);
    return [
      {
        type: 'group',
        label: t('Navigation.r250401.History'),
        isHide: !isLoggedIn,
        children: [
          {
            type: 'menu',
            label: t('Navigation.r250401.Perpetual_history'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: !isLoggedIn || isSpotOnly,
            children: [
              { type: 'link', label: t('Navigation.r250401.Position'), url: '/history/perpetual/position' },
              { type: 'link', label: t('Navigation.r250401.Pnl'), url: '/history/perpetual/pnl' },
              { type: 'link', label: t('Navigation.r250401.Funding'), url: '/history/perpetual/funding' },
              { type: 'link', label: t('Navigation.r250401.Trade'), url: '/history/perpetual/trade' },
              { type: 'link', label: t('Navigation.r250401.Profit_boost'), url: '/history/perpetual/profit-boost' },
              { type: 'link', label: t('Navigation.r250401.Airdrop'), url: '/history/perpetual/airdrop' },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Spot_history'),
            style: { gridColumn: 1, gridRow: isSpotOnly ? 1 : 2 },
            isHide: !isLoggedIn,
            children: [
              { type: 'link', label: t('Navigation.r250401.Pnl'), url: '/history/spot/pnl' },
              { type: 'link', label: t('Navigation.r250401.Trade'), url: '/history/spot/trade' },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Transaction_history'),
            style: { gridColumn: 2, gridRow: '1/3' },
            isHide: !isLoggedIn,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Earn'),
                url: '/history/transaction/earn',
                isHide: isSpotOnly,
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Launchpool_reward'),
                url: '/history/transaction/launchpool-reward',
                isHide: isSpotOnly,
              },
              { type: 'link', label: t('Navigation.r250401.Deposit'), url: '/history/transaction/deposit' },
              { type: 'link', label: t('Navigation.r250401.Withdrawal'), url: '/history/transaction/withdrawal' },
              { type: 'link', label: t('Navigation.r250401.Conversion'), url: '/history/transaction/conversion' },
              {
                type: 'link',
                label: t('Navigation.r250818.Referral_affiliate'),
                url: '/history/transaction/referral-and-affiliate',
                isHide: isSpotOnly,
              },
            ],
          },
        ],
      },
    ];
  });

  return { historyNavAtom };
});

const moreNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyAtom } = bunja.use(navBaseBunja);
  const { isUsd1PointsDashboardEnabledAtom } = bunja.use(navAbTestBunja);
  const { myOptInIntervalRankingsAtom } = bunja.use(tradingLeaderboardMyRankingBunja);

  const competitionsAtom = atom<TradingCompetition[]>((get) => get(competitionsQueryAtom).data || []);
  const competitionsQueryAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: ['getTradingCompetitionsListApi', 'GNB'],
      queryFn: () => getTradingCompetitionsListApi(),
      select: (res: AxiosResponse<{ competitions: TradingCompetition[] }, unknown, {}>) =>
        res.data.competitions
          .filter(
            (c) => (c.competitionStatus === 'UPCOMING' || c.competitionStatus === 'ONGOING') && !!c.participationTime,
          )
          .sort((a, b) => new Date(a.competitionStartTime).getTime() - new Date(b.competitionStartTime).getTime()),
      enabled: isLoggedIn,
    };
  });

  const moreNavAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyAtom);
    const competitions = get(competitionsAtom);
    const isUsd1PointsDashboardEnabled = get(isUsd1PointsDashboardEnabledAtom);
    const myTradingLeaderboardsRanking = get(myOptInIntervalRankingsAtom);
    return [
      {
        type: 'group',
        label: t('Navigation.r250401.More'),
        children: [
          {
            type: 'menu',
            label: t('Navigation.r250401.Events'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: isSpotOnly,
            children: (
              [
                {
                  type: 'link',
                  label: t('Navigation.r250401.Referral_program'),
                  url: '/marketing/referral',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Vip_program'),
                  url: '/marketing/vip',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Affiliate_program'),
                  url: '/marketing/affiliates',
                  newTab: true,
                },
              ] satisfies (LeaderboardNav | LinkNav | false)[]
            ).filter(Boolean),
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Challenges'),
            style: { gridColumn: 1, gridRow: 2 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Sparks_auction'),
                url: '/spark/auction',
              },
              ...(isLoggedIn
                ? competitions.map<LinkNav>((c) => ({
                    type: 'link',
                    label: c.title,
                    url: `/competitions/${c.competitionUrl}`,
                  }))
                : []),
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250911.Wlfi_x_flipster_points'),
            style: { gridColumn: 1, gridRow: 3 },
            isHide: !isUsd1PointsDashboardEnabled,
            children: [{ type: 'link', label: t('Navigation.r250911.Usd1_points_program'), url: '/usd1-points' }],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Pnl_leaderboard'),
            style: { gridColumn: 2, gridRow: 1 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Weekly'),
                url: `/trading-leaderboard?interval=WEEKLY&tier=${myTradingLeaderboardsRanking?.WEEKLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.WEEKLY,
              },
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Monthly'),
                url: `/trading-leaderboard?interval=MONTHLY&tier=${myTradingLeaderboardsRanking?.MONTHLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.MONTHLY,
              },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Learn'),
            style: isSpotOnly ? { gridColumn: 1, gridRow: 1 } : { gridColumn: 2, gridRow: 2 },
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Blog'),
                url: '/blog',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Glossary'),
                url: '/glossary',
              },
            ],
          },
        ],
      },
    ];
  });

  return { moreNavAtom };
});
